import React, { useEffect, useState, useRef, useMemo } from 'react'
import {
    APARTMENT,
    axiosInstance,
    formatPhoneForDisplay,
    isAccountManager,
    toMonthYear,
} from '../../helpers'
import FileDownload from 'js-file-download'

//components
import {
    Container,
    EzosLoader,
    SideDrawer,
    SideDrawerContainer,
} from '../../components'
import {
    useTheme,
    Tooltip,
    IconButton,
    Popover,
    TextField,
    InputAdornment,
    Button,
    CircularProgress,
    Tabs,
    Tab,
    Badge,
} from '@material-ui/core'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import GetAppIcon from '@material-ui/icons/GetApp'
import PublishIcon from '@material-ui/icons/Publish'
import SearchIcon from '@material-ui/icons/Search'
import ScheduleIcon from '@material-ui/icons/Schedule'
import AssignmentIcon from '@material-ui/icons/Assignment'
import CallMergeIcon from '@material-ui/icons/CallMerge'

//hooks
import {
    AppDispatch,
    useAppDispatch,
    useCompany,
    useService,
    useUser,
} from '../../hooks'
import { usePricing } from '../../hooks/usePricing'

//types
import {
    User,
    ListVendor,
    PermissionRole,
    EDIT_PERMISSION_ROLES,
    AreaConfig,
    UnitConfig,
    IdBoolMap,
    ModelMap,
    ApartmentVendor,
    ServiceContract,
    getScCompoundKeyString,
    ServiceContractGroup,
} from '../../models'

import { useServiceContract } from '../../hooks/useServiceContract'
import { getPermissionRoleList } from '../../store/permissions'

import { PermissionRoleDrawer } from './PermissionRoleDrawer'

import { toast } from 'react-toastify'
import { UserModal } from './UserModal'
import { BulkCreateUsersModal } from './BulkCreateUsersModal'
import { BulkArchiveUsersModal } from './BulkArchiveUsersModal'
import { WorkForceVendorTable } from './WorkforceVendorTable'
import { WorkforceNewPermissionTable } from './WorkforcePermissionTable'
import { WorkforceTeamTable } from './WorkforceTeamTable'
import { VendorAnalyticsDrawer } from './VendorAnalyticsDrawer'
import { useSelector } from 'react-redux'
import { getAreaConfigList, getUnitConfigList, RootState } from '../../store'
import { hasPermission } from '../../models/Users/services'
import { MergeVendorsModal } from '../Invoice/MergeVendorsModal'
import { VendorModal } from './VendorModal/VendorModal'
import { VendorRecommendModal } from './VendorRecommendModal'
import { RequestRFPModal } from './RequestRFPModal'
import { LoriIcon } from '../../navigation/layouts/Lori/Icons/LoriIcon'
import { useHistory, useLocation } from 'react-router-dom'
import { UserState } from '../../components/AdminPanel/WorkspaceModal/SelectedWorkspacePanel'
import { AddVendorUser } from './AddVendorUser'
import { VendorPricingModal } from './VendorPricing/VendorPricingModal'
import { PendingPricingModal } from './VendorPricing/PendingPricingModal'
import { MARGIN } from '../../components/TemplateScheduleGantt/constants'
import { defaultThemes } from 'react-data-table-component'
import { typographyStyle } from '../../theme/typographyStyle'

export const NEW_VENDOR_ID = 0

export const Workforce = () => {
    const {
        getVendorList,
        vendorList,
        apartmentVendorList,
        updateVendor,
        updateVendorServices,
        createVendor,
        getApartmentVendorList,
        getRecommendVendorsForProperty,
        requestUpdatedPricing,
        addVendorToApartment,
    } = useCompany()

    const { getServiceContractList } = useServiceContract()
    const { serviceList, getServiceList, createService } = useService()
    const { actions, userList, workspaceUser } = useUser()
    const { getUserList } = actions
    const permissionRoleList = useSelector(
        (state: RootState) => state.permission.permissionRoleList,
    )
    const theme = useTheme()
    const dispatch = useAppDispatch()

    const history = useHistory()
    const location = useLocation()

    const VENDOR_TAB = 0
    const MY_TEAM = 1
    const PERMISSION_ROLES_TAB = 2

    const tabToHash: { [key: number]: string } = {
        [VENDOR_TAB]: 'my-vendors',
        [MY_TEAM]: 'my-team',
        [PERMISSION_ROLES_TAB]: 'permissions',
    }

    const hashToTab = Object.fromEntries(
        Object.entries(tabToHash).map(([key, value]) => [value, parseInt(key)]),
    )

    useEffect(() => {
        const hash = location.hash.replace('#', '')
        if (hash in hashToTab) {
            setSelectedTab(hashToTab[hash])
        }
    }, [location])

    const [selectedTab, setSelectedTab] = useState<number>(() => {
        const hash = location.hash.replace('#', '')
        return hash in hashToTab
            ? hashToTab[hash]
            : workspaceUser?.active_workspace.company_type === 'APARTMENT'
            ? VENDOR_TAB
            : MY_TEAM
    })

    const [openDrawer, setOpenDrawer] = useState(false)
    const [editUser, setEditUser] = useState<User>()
    const [editVendor, setEditVendor] = useState<ListVendor | undefined>()
    const [
        editPermissionRole,
        setEditPermissionRole,
    ] = useState<PermissionRole>()

    const [apartmentVendorMap, setApartmentVendorMap] = useState<
        Record<number, ApartmentVendor>
    >({})

    const [searchText, setSearchText] = useState('')

    const [popoverOpen, setPopoverOpen] = useState(false)
    const popoverRef = useRef<HTMLDivElement>(null)
    const uploadUnitSheetRef = useRef<HTMLInputElement>(null)

    const [unitConfigList, setUnitConfigList] = useState<UnitConfig[]>([])
    const [areaConfigList, setAreaConfigList] = useState<AreaConfig[]>([])

    const [vendor, setVendor] = useState<ListVendor | undefined>()

    const [formState, setFormState] = useState<WorkforceFormState>(
        closedFormState,
    )

    const [loadingState, setLoadingState] = useState(true)

    const [openBulkCreateUsers, setOpenBulkCreateUsers] = useState(false)
    const [openBulkArchiveUsers, setOpenBulkArchiveUsers] = useState(false)

    const [openMergeVendors, setOpenMergeVendors] = useState(false)
    const [openVendorRecommendModal, setOpenVendorRecommendModal] = useState(
        false,
    )
    const [openRequestRFPModal, setOpenRequestRFPModal] = useState(false)

    const [vendorUserState, setVendorUserState] = useState<UserState>({
        loading: false,
        userList: [],
    })

    const {
        fetchPricingContracts,
        pendingServiceContractGroups,
        fetchPendingPricingRequests,
        setPendingServiceContractGroups,
    } = usePricing()

    const [openVendorUserDrawer, setOpenVendorUserDrawer] = useState(false)
    const [showPendingPricingPanel, setShowPendingPricingPanel] = useState(
        false,
    )
    const [
        selectedPendingVendor,
        setSelectedPendingVendor,
    ] = useState<ListVendor | null>(null)

    const WORKFORCE_DRAWER_WIDTH = 360

    useMemo(() => {
        const apartmentVendorMap: Record<number, ApartmentVendor> = {}
        apartmentVendorList.forEach((aptVendor: ApartmentVendor) => {
            apartmentVendorMap[aptVendor.vendor.id] = aptVendor
        })
        setApartmentVendorMap(apartmentVendorMap)
    }, [apartmentVendorList])

    useEffect(() => {
        if (workspaceUser?.active_workspace.company_type === 'APARTMENT') {
            const vendorListPromise = getVendorList({
                params: { my_team: true },
            })
            const serviceListPromise = getServiceList({})
            const userListPromise = getUserList({ params: { my_team: true } })
            const areaListPromise = dispatch(
                getAreaConfigList({ shows_on_schedule: true, minimal: true }),
            )
            const aptVendorPromise = getApartmentVendorList()
            const unitListPromise = dispatch(
                getUnitConfigList({ shows_on_schedule: true }),
            )

            const pendingPricingRequestsPromise = fetchPendingPricingRequests()

            dispatch(getPermissionRoleList({}))

            Promise.all([
                vendorListPromise,
                serviceListPromise,
                userListPromise,
                areaListPromise,
                unitListPromise,
                aptVendorPromise,
            ])
                .then((values) => {
                    const vendorList = values[0].data
                    const serviceList = values[1].data
                    const areaConfigList = values[3].data
                    const unitConfigList = values[4].data
                    setUnitConfigList(unitConfigList)
                    setAreaConfigList(areaConfigList)
                })
                .catch((e) => {
                    console.log(e)
                })
                .finally(() => {
                    setLoadingState(false)
                })
        } else {
            const userListPromise = getUserList({ params: { my_team: true } })
            dispatch(getPermissionRoleList({}))

            Promise.all([userListPromise])
                .catch((e) => {
                    console.log(e)
                })
                .finally(() => {
                    setLoadingState(false)
                })
        }
    }, [])

    // Set first pending vendor as selected when data loads
    useEffect(() => {
        if (pendingServiceContractGroups.length > 0 && !selectedPendingVendor) {
            const firstVendor = pendingServiceContractGroups[0].vendor
            setSelectedPendingVendor(firstVendor)
        }
    }, [pendingServiceContractGroups, selectedPendingVendor])

    const HeadCellStyle: React.CSSProperties = {
        fontWeight: 600,
        color: theme.palette.darkGreen.main,
        fontSize: '17px',
        flex: 2,
    }

    const CellStyle: React.CSSProperties = {
        fontWeight: 400,
        fontSize: '15px',
        flex: 1,
    }

    const USER_CAN_EDIT_PERMISSION_ROLES = hasPermission(
        workspaceUser,
        EDIT_PERMISSION_ROLES,
    )

    const resendWelcomeEmail = (vendor: ListVendor) => {
        const body = {
            email: vendor.email,
            vendor_id: vendor.id,
        }
        axiosInstance
            .post(`company/vendor/resend_vendor_welcome_email/`, body)
            .then(() => {
                toast.success(`Welcome email resent to ${vendor.name}`)
            })
            .catch((e) => {
                toast.error(e.response.data.message)
            })
    }

    const handleTabChange = (_: any, newValue: number) => {
        if (newValue !== selectedTab) {
            setSelectedTab(newValue)
            history.push(`/workforce#${tabToHash[newValue]}`)
        }
    }

    const accountManager = isAccountManager(workspaceUser)

    const getVendorUserList = (vendor: ListVendor) => {
        axiosInstance
            .get(`company/vendor/${vendor.id}/get_vendor_users/`, {
                params: {
                    my_team: true,
                },
            })
            .then((res) => {
                setVendorUserState({ loading: false, userList: res.data })
            })
    }

    const onClickAddVendorUser = (vendor: ListVendor) => {
        setOpenVendorUserDrawer(true)
        getVendorUserList(vendor)
        setVendor(vendor)
    }

    const downloadTeamCSV = () => {
        const headers = ['Name', 'Date', 'Role', 'Email', 'Phone']
        const csvContent = [
            headers.join(','),
            ...userList.map((user) => {
                const date = toMonthYear(new Date(user.created_date))
                let phone = ''
                let roles_str = user.permission_roles.reduce(
                    (prev, role) => prev.concat(`${role.name}, `),
                    '',
                )
                roles_str = roles_str.slice(0, -2)
                if (user.cell_phone) {
                    phone = formatPhoneForDisplay(user.cell_phone)
                } else if (user.phone) {
                    phone = formatPhoneForDisplay(user.phone)
                }
                return [
                    `"${user.name}"`,
                    `"${date}"`,
                    `"${roles_str}"`,
                    `"${user.email}"`,
                    `"${phone}"`,
                ].join(',')
            }),
        ].join('\n')

        const blob = new Blob([csvContent], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${workspaceUser?.active_workspace.name}_users.csv`
        a.click()
        window.URL.revokeObjectURL(url)
    }

    const downloadVendorsCSV = () => {
        const headers = [
            'Name',
            'Address',
            'Type',
            'Services',
            'Phone',
            'Email',
        ]
        const csvContent = [
            headers.join(','),
            ...vendorList.map((vendor) => {
                const type = vendor.in_house ? 'In House' : 'Contractor'

                // Format address like in VendorRow
                const fullAddress = `${vendor.address}, ${vendor.city} ${vendor.state}, ${vendor.zip_code}`

                // Format services - show all services that the vendor offers
                let services_str = vendor.services.reduce(
                    (prev, service) => prev.concat(`${service.name}, `),
                    '',
                )
                services_str = services_str.slice(0, -2) // Remove trailing comma and space

                return [
                    `"${vendor.name}"`,
                    `"${fullAddress}"`,
                    `"${type}"`,
                    `"${services_str}"`,
                    `"${formatPhoneForDisplay(vendor.phone)}"`,
                    `"${vendor.email || ''}"`,
                ].join(',')
            }),
        ].join('\n')

        const blob = new Blob([csvContent], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${workspaceUser?.active_workspace.name}_vendors.csv`
        a.click()
        window.URL.revokeObjectURL(url)
    }

    const onClickRequestUpdatedPricing = (vendor: ListVendor) => {
        requestUpdatedPricing({
            body: {
                vendor_id: vendor.id,
            },
        })
        fetchPricingContracts(vendor.id)
    }

    const vendorMap = useMemo(() => {
        return vendorList.reduce((acc, vendor) => {
            acc[vendor.id] = vendor
            return acc
        }, {} as ModelMap<ListVendor>)
    }, [vendorList])

    return (
        <SideDrawerContainer open={false} width={WORKFORCE_DRAWER_WIDTH}>
            <Container
                flex={1}
                direction="column"
                style={{ height: 'calc(100vh - 104px)' }}
            >
                {/* Header */}
                <Container style={{ width: '100%' }}>
                    <Container>
                        <span
                            style={{
                                ...typographyStyle(theme.typography.h4),
                                fontWeight: 700,
                                marginBottom: theme.spacing(1),
                            }}
                        >
                            {' '}
                            Workforce
                        </span>
                    </Container>

                    <div style={{ flex: 1 }} />

                    <Popover
                        id="UPSTREAM_FINANCE_POPOVER"
                        open={popoverOpen}
                        anchorEl={popoverRef.current}
                        onClose={() => setPopoverOpen(false)}
                    >
                        <Container
                            style={{
                                backgroundColor: theme.palette.grey[100],
                                flexDirection: 'column',
                                padding: theme.spacing(2),
                            }}
                        >
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.h5),
                                    fontWeight: 700,
                                }}
                            >
                                Upstream Contract Sheet
                            </span>

                            <Container
                                style={{
                                    alignItems: 'center',
                                }}
                            >
                                <Tooltip title="Download upstream contract sheet">
                                    <IconButton
                                        onClick={() => {
                                            axiosInstance
                                                .get(
                                                    'service-contract/upstream-contract-sheet/',
                                                    {
                                                        responseType: 'blob',
                                                    },
                                                )
                                                .then((response) => {
                                                    FileDownload(
                                                        new Blob([
                                                            response.data,
                                                        ]),
                                                        'upstream_contract.xlsx',
                                                    )
                                                })
                                        }}
                                    >
                                        <GetAppIcon />
                                    </IconButton>
                                </Tooltip>

                                <span>Download</span>

                                <div style={{ flex: 1 }} />

                                <Tooltip title="Upload upstream contract sheet">
                                    <IconButton
                                        onClick={() =>
                                            uploadUnitSheetRef.current?.click()
                                        }
                                    >
                                        <PublishIcon />
                                    </IconButton>
                                </Tooltip>

                                <span>Upload</span>
                            </Container>
                        </Container>
                    </Popover>
                </Container>

                <Tabs value={selectedTab} onChange={handleTabChange}>
                    {workspaceUser?.active_workspace.company_type ===
                        'APARTMENT' && (
                        <Tab label="My Vendors" value={VENDOR_TAB} />
                    )}
                    <Tab label="My Team" value={MY_TEAM} />
                    <Tab
                        label="Permission Roles"
                        value={PERMISSION_ROLES_TAB}
                    />
                </Tabs>

                {/* Tables */}
                {(loadingState && (
                    <Container
                        style={{
                            flex: 1,
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <EzosLoader />
                    </Container>
                )) || (
                    <Container direction="column">
                        <Container>
                            {selectedTab === MY_TEAM && (
                                <Container
                                    style={{ flexDirection: 'column', flex: 1 }}
                                >
                                    <Container
                                        style={{
                                            marginTop: theme.spacing(2),
                                            fontSize: '15px',
                                            fontWeight: 400,
                                        }}
                                    >
                                        View all of your workers within the team
                                    </Container>

                                    <Container>
                                        <TextField
                                            variant="outlined"
                                            placeholder="Search"
                                            value={searchText}
                                            onChange={(e) =>
                                                setSearchText(e.target.value)
                                            }
                                            size="small"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            style={{
                                                margin: theme.spacing(
                                                    3,
                                                    0,
                                                    2,
                                                    0,
                                                ),
                                            }}
                                        />
                                        <Button
                                            variant="contained"
                                            style={{
                                                margin: theme.spacing(
                                                    3,
                                                    0,
                                                    2,
                                                    2,
                                                ),
                                                backgroundColor: '#008C85',
                                                color: 'white',
                                                textTransform: 'none',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => {
                                                setOpenDrawer(true)
                                                setEditUser(undefined)
                                                setEditVendor(undefined)
                                                setEditPermissionRole(undefined)
                                            }}
                                        >
                                            + Add Member
                                        </Button>
                                        <Button
                                            variant="contained"
                                            style={{
                                                margin: theme.spacing(
                                                    3,
                                                    0,
                                                    2,
                                                    2,
                                                ),
                                                backgroundColor: '#008C85',
                                                color: 'white',
                                                textTransform: 'none',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() =>
                                                setOpenBulkCreateUsers(true)
                                            }
                                        >
                                            + Bulk Add
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            style={{
                                                margin: theme.spacing(
                                                    3,
                                                    0,
                                                    2,
                                                    2,
                                                ),
                                                color: theme.palette.error.main,
                                                borderColor:
                                                    theme.palette.error.main,
                                                textTransform: 'none',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() =>
                                                setOpenBulkArchiveUsers(true)
                                            }
                                        >
                                            Bulk Archive
                                        </Button>
                                        <div style={{ flex: 1 }} />
                                        <Tooltip title="Download Team CSV">
                                            <IconButton
                                                onClick={downloadTeamCSV}
                                                style={{
                                                    margin: theme.spacing(
                                                        3,
                                                        0,
                                                        2,
                                                        0,
                                                    ),
                                                }}
                                            >
                                                <GetAppIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Container>
                                    <WorkforceTeamTable
                                        headCellStyle={HeadCellStyle}
                                        userList={userList.filter((user) =>
                                            user.name
                                                .toLocaleLowerCase()
                                                .includes(
                                                    searchText.toLocaleLowerCase(),
                                                ),
                                        )}
                                        cellStyle={CellStyle}
                                        setEditUser={setEditUser}
                                        setOpenDrawer={setOpenDrawer}
                                    />
                                </Container>
                            )}
                            {selectedTab === VENDOR_TAB && (
                                <Container
                                    style={{ flexDirection: 'column', flex: 1 }}
                                >
                                    <Container
                                        style={{
                                            marginTop: theme.spacing(2),
                                            fontSize: '15px',
                                            fontWeight: 400,
                                        }}
                                    >
                                        View all of your vendors around your
                                        area and schedule them out for work!
                                    </Container>

                                    <Container style={{ flex: 1 }}>
                                        <TextField
                                            variant="outlined"
                                            placeholder="Search"
                                            value={searchText}
                                            onChange={(e) =>
                                                setSearchText(e.target.value)
                                            }
                                            size="small"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            style={{
                                                margin: theme.spacing(
                                                    3,
                                                    0,
                                                    2,
                                                    0,
                                                ),
                                            }}
                                        />
                                        <Button
                                            variant="contained"
                                            style={{
                                                margin: theme.spacing(
                                                    3,
                                                    0,
                                                    2,
                                                    2,
                                                ),
                                                backgroundColor: '#008C85',
                                                color: 'white',
                                                textTransform: 'none',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => {
                                                setFormState({
                                                    ...formState,
                                                    modal: {
                                                        ...formState.modal,
                                                        vendorModal: {
                                                            open: true,
                                                            vendorId: -1,
                                                        },
                                                    },
                                                })
                                            }}
                                        >
                                            + Add Vendor
                                        </Button>

                                        <Tooltip title="Get vendor recommendations for your property">
                                            <Button
                                                variant="contained"
                                                style={{
                                                    margin: theme.spacing(
                                                        3,
                                                        0,
                                                        2,
                                                        2,
                                                    ),
                                                    backgroundColor: '#008C85',
                                                    color: 'white',
                                                    textTransform: 'none',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                                onClick={() =>
                                                    setOpenVendorRecommendModal(
                                                        true,
                                                    )
                                                }
                                            >
                                                <LoriIcon
                                                    width={20}
                                                    height={20}
                                                    color="white"
                                                />
                                                <span
                                                    style={{
                                                        marginLeft: theme.spacing(
                                                            1,
                                                        ),
                                                    }}
                                                >
                                                    Recommended Vendors
                                                </span>
                                            </Button>
                                        </Tooltip>
                                        <Button
                                            variant="contained"
                                            style={{
                                                margin: theme.spacing(
                                                    3,
                                                    0,
                                                    2,
                                                    2,
                                                ),
                                                backgroundColor:
                                                    theme.palette.primary.dark,
                                                color: 'white',
                                                textTransform: 'none',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                            onClick={() =>
                                                setOpenRequestRFPModal(true)
                                            }
                                        >
                                            <AssignmentIcon
                                                style={{ fontSize: 20 }}
                                            />
                                            <span
                                                style={{
                                                    marginLeft: theme.spacing(
                                                        1,
                                                    ),
                                                }}
                                            >
                                                Request RFPs
                                            </span>
                                        </Button>

                                        {isAccountManager(workspaceUser) && (
                                            <Button
                                                variant="contained"
                                                style={{
                                                    margin: theme.spacing(
                                                        3,
                                                        2,
                                                        2,
                                                        2,
                                                    ),
                                                    backgroundColor: '#8A8A8A',
                                                    color: 'white',
                                                    textTransform: 'none',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                                onClick={() =>
                                                    setOpenMergeVendors(true)
                                                }
                                            >
                                                <CallMergeIcon
                                                    style={{ fontSize: 20 }}
                                                />
                                                <span
                                                    style={{
                                                        marginLeft: theme.spacing(
                                                            1,
                                                        ),
                                                    }}
                                                >
                                                    Merge Vendors
                                                </span>
                                            </Button>
                                        )}
                                        <div style={{ flex: 1 }} />
                                        <Tooltip title="Pending Pricing Requests">
                                            <IconButton
                                                onClick={() => {
                                                    setShowPendingPricingPanel(
                                                        true,
                                                    )
                                                    if (
                                                        pendingServiceContractGroups.length >
                                                        0
                                                    ) {
                                                        const firstVendor =
                                                            pendingServiceContractGroups[0]
                                                                .vendor
                                                        setSelectedPendingVendor(
                                                            firstVendor,
                                                        )
                                                        setVendor(firstVendor)
                                                        setFormState({
                                                            ...formState,
                                                            modal: {
                                                                ...formState.modal,
                                                                vendorPricing: {
                                                                    open: true,
                                                                    vendorId:
                                                                        firstVendor.id,
                                                                },
                                                            },
                                                        })
                                                    }
                                                }}
                                                style={{
                                                    margin: theme.spacing(
                                                        3,
                                                        1,
                                                        2,
                                                        0,
                                                    ),
                                                }}
                                            >
                                                <Badge
                                                    badgeContent={
                                                        pendingServiceContractGroups.length
                                                    }
                                                    color="error"
                                                >
                                                    <ScheduleIcon />
                                                </Badge>
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Download Vendors CSV">
                                            <IconButton
                                                onClick={downloadVendorsCSV}
                                                style={{
                                                    margin: theme.spacing(
                                                        3,
                                                        0,
                                                        2,
                                                        0,
                                                    ),
                                                }}
                                            >
                                                <GetAppIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Container>
                                    <WorkForceVendorTable
                                        headCellStyle={HeadCellStyle}
                                        vendorList={vendorList.filter(
                                            (vendor) =>
                                                vendor.name
                                                    .toLocaleLowerCase()
                                                    .includes(
                                                        searchText.toLocaleLowerCase(),
                                                    ),
                                        )}
                                        cellStyle={CellStyle}
                                        setEditVendor={setEditVendor}
                                        setOpenDrawer={setOpenDrawer}
                                        apartmentVendorMap={apartmentVendorMap}
                                        onRatingSaved={() => {
                                            getApartmentVendorList()
                                        }}
                                        onClickVendorRow={(vendor) => {
                                            setVendor(vendor)
                                            setFormState({
                                                ...formState,
                                                modal: {
                                                    ...formState.modal,
                                                    vendorModal: {
                                                        open: true,
                                                        vendorId: vendor.id,
                                                    },
                                                },
                                            })
                                        }}
                                        setOpenAnalytics={(vendor) => {
                                            setVendor(vendor)
                                            setFormState({
                                                ...formState,
                                                drawer: {
                                                    ...formState.drawer,
                                                    vendorAnalyticsDrawer: {
                                                        open: true,
                                                        vendorId: vendor.id,
                                                    },
                                                },
                                            })
                                        }}
                                        resendWelcomeEmail={resendWelcomeEmail}
                                        onClickAddVendorUser={
                                            onClickAddVendorUser
                                        }
                                        onClickRequestUpdatedPricing={
                                            onClickRequestUpdatedPricing
                                        }
                                        setOpenPricing={(vendor, index) => {
                                            setVendor(vendor)
                                            setFormState({
                                                ...formState,
                                                modal: {
                                                    ...formState.modal,
                                                    vendorPricing: {
                                                        open: true,
                                                        vendorId: vendor.id,
                                                    },
                                                },
                                            })
                                        }}
                                    />
                                </Container>
                            )}
                            {selectedTab === PERMISSION_ROLES_TAB && (
                                <Container
                                    style={{
                                        flexDirection: 'column',
                                        flex: 1,
                                    }}
                                >
                                    <Container
                                        style={{
                                            marginTop: theme.spacing(2),
                                            fontSize: '15px',
                                            fontWeight: 400,
                                        }}
                                    >
                                        View your current permission roles down
                                        below
                                    </Container>

                                    <Container>
                                        <TextField
                                            variant="outlined"
                                            placeholder="Search"
                                            value={searchText}
                                            onChange={(e) =>
                                                setSearchText(e.target.value)
                                            }
                                            size="small"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            style={{
                                                margin: theme.spacing(
                                                    3,
                                                    0,
                                                    2,
                                                    0,
                                                ),
                                            }}
                                        />
                                        {USER_CAN_EDIT_PERMISSION_ROLES && (
                                            <Button
                                                variant="contained"
                                                style={{
                                                    margin: theme.spacing(
                                                        3,
                                                        0,
                                                        2,
                                                        2,
                                                    ),
                                                    backgroundColor: '#008C85',
                                                    color: 'white',
                                                    textTransform: 'none',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() => {
                                                    setOpenDrawer(true)
                                                    setEditUser(undefined)
                                                    setEditVendor(undefined)
                                                    setEditPermissionRole(
                                                        undefined,
                                                    )
                                                }}
                                            >
                                                + Add Role
                                            </Button>
                                        )}

                                        <Container style={{ flex: 1 }} />

                                        <Tooltip title="Download Permission Roles">
                                            <IconButton
                                                style={{
                                                    marginRight: theme.spacing(
                                                        1,
                                                    ),
                                                }}
                                                size="small"
                                                onClick={() => {
                                                    axiosInstance
                                                        .get(
                                                            'permission/get_permission_role_excel_sheet/',
                                                            {
                                                                responseType:
                                                                    'blob',
                                                            },
                                                        )
                                                        .then((response) => {
                                                            FileDownload(
                                                                new Blob([
                                                                    response.data,
                                                                ]),
                                                                `PermissionRoles.xlsx`,
                                                            )
                                                        })
                                                }}
                                            >
                                                <GetAppIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Container>
                                    <WorkforceNewPermissionTable
                                        permissionRoleList={
                                            permissionRoleList?.filter((role) =>
                                                role.name
                                                    .toLocaleLowerCase()
                                                    .includes(
                                                        searchText.toLocaleLowerCase(),
                                                    ),
                                            ) ?? []
                                        }
                                        cellStyle={CellStyle}
                                        headCellStyle={HeadCellStyle}
                                        setOpenDrawer={setOpenDrawer}
                                        setEditPermissionRole={(role) => {
                                            setEditPermissionRole(role)
                                            setOpenDrawer(true)
                                        }}
                                    />
                                </Container>
                            )}
                        </Container>
                    </Container>
                )}
            </Container>

            {/* side drawer */}
            {selectedTab === VENDOR_TAB && vendor && (
                <VendorAnalyticsDrawer
                    vendorList={vendorList}
                    formState={formState}
                    handleClose={() => setFormState(closedFormState)}
                    width={WORKFORCE_DRAWER_WIDTH}
                />
            )}

            {/* pricing modal */}
            {selectedTab === VENDOR_TAB && vendor && !showPendingPricingPanel && (
                <VendorPricingModal
                    vendor={vendor}
                    formState={formState}
                    handleClose={() => {
                        setFormState(closedFormState)
                        setShowPendingPricingPanel(false)
                        setSelectedPendingVendor(null)
                    }}
                    unitConfigs={unitConfigList}
                    areaConfigs={areaConfigList}
                    serviceList={serviceList}
                    onClickRequestUpdatedPricing={onClickRequestUpdatedPricing}
                />
            )}

            {/* pending pricing modal */}
            {selectedTab === VENDOR_TAB && showPendingPricingPanel && (
                <PendingPricingModal
                    open={showPendingPricingPanel}
                    unitConfigs={unitConfigList}
                    areaConfigs={areaConfigList}
                    pendingServiceContractGroups={pendingServiceContractGroups}
                    selectedPendingVendor={selectedPendingVendor}
                    vendorMap={vendorMap}
                    onClose={() => {
                        setFormState(closedFormState)
                        setShowPendingPricingPanel(false)
                        setSelectedPendingVendor(null)
                    }}
                    onPendingVendorSelect={setSelectedPendingVendor}
                    onClickRequestUpdatedPricing={onClickRequestUpdatedPricing}
                    setPendingServiceContractGroups={
                        setPendingServiceContractGroups
                    }
                    addVendorToApartment={addVendorToApartment}
                />
            )}

            {selectedTab === VENDOR_TAB && (
                <VendorModal
                    formState={formState}
                    theme={theme}
                    vendorList={vendorList}
                    onClose={() => setFormState(closedFormState)}
                    serviceList={serviceList}
                    unitConfigs={unitConfigList}
                    areaConfigs={areaConfigList}
                    updateVendor={updateVendor}
                    updateVendorServices={updateVendorServices}
                    createVendor={createVendor}
                    createService={createService}
                    apartmentVendorMap={apartmentVendorMap}
                    setApartmentVendorMap={setApartmentVendorMap}
                />
            )}

            {selectedTab === MY_TEAM && (
                <UserModal
                    handleClose={() => setOpenDrawer(false)}
                    drawerUser={editUser}
                    open={openDrawer}
                />
            )}

            {selectedTab === MY_TEAM && (
                <BulkCreateUsersModal
                    open={openBulkCreateUsers}
                    handleClose={() => setOpenBulkCreateUsers(false)}
                />
            )}

            {selectedTab === MY_TEAM && (
                <BulkArchiveUsersModal
                    open={openBulkArchiveUsers}
                    handleClose={() => setOpenBulkArchiveUsers(false)}
                    userList={userList}
                />
            )}

            {selectedTab === PERMISSION_ROLES_TAB && workspaceUser && (
                <SideDrawer
                    open={openDrawer}
                    width={WORKFORCE_DRAWER_WIDTH}
                    handleClose={() => setOpenDrawer(false)}
                    header={<></>}
                >
                    <PermissionRoleDrawer
                        permissionRole={editPermissionRole}
                        handleClose={() => setOpenDrawer(false)}
                        open={openDrawer}
                        organization={
                            workspaceUser.active_workspace.organization
                        }
                    />
                </SideDrawer>
            )}

            {selectedTab === VENDOR_TAB && accountManager && (
                <MergeVendorsModal
                    open={openMergeVendors}
                    handleClose={() => setOpenMergeVendors(false)}
                    vendors={vendorList}
                />
            )}

            {selectedTab === VENDOR_TAB && (
                <VendorRecommendModal
                    open={openVendorRecommendModal}
                    onClose={() => setOpenVendorRecommendModal(false)}
                    theme={theme}
                    fetchRecommendedVendors={getRecommendVendorsForProperty}
                    onAddVendor={(vendor) => {
                        return addVendorToApartment({
                            body: { vendor: vendor.id },
                        }).then(() => {
                            getApartmentVendorList()
                            getVendorList({ params: { my_team: true } })
                            toast.success(
                                `${vendor.name} added to your property.`,
                            )
                        })
                    }}
                />
            )}

            {selectedTab === VENDOR_TAB && (
                <RequestRFPModal
                    open={openRequestRFPModal}
                    onClose={() => setOpenRequestRFPModal(false)}
                />
            )}

            <input
                ref={uploadUnitSheetRef}
                type="file"
                style={{ display: 'none' }}
                accept={'.xlsx'}
                onChange={(e) => {
                    if (
                        e.target.files?.length !== undefined &&
                        e.target.files.length > 0
                    ) {
                        const sheet = e.target.files[0]

                        const reqBody = new FormData()
                        reqBody.append('sheet', sheet)

                        axiosInstance
                            .post(
                                'service-contract/upstream-contract-sheet/',
                                reqBody,
                            )
                            .then(() => {
                                toast.success(
                                    'Success you will receive an email with your upload report soon.',
                                )
                            })
                            .catch(() => {
                                toast.error(
                                    'There was a problem with your sheet.',
                                )
                            })
                    }
                }}
            />

            {selectedTab === VENDOR_TAB && vendor && (
                <AddVendorUser
                    theme={theme}
                    vendor={vendor}
                    vendorUserState={vendorUserState}
                    openVendorUserDrawer={openVendorUserDrawer}
                    setVendorUserState={setVendorUserState}
                    setOpenVendorUserDrawer={setOpenVendorUserDrawer}
                />
            )}
        </SideDrawerContainer>
    )
}

export type WorkforceFormState = {
    modal: {
        vendorModal: {
            open: boolean
            vendorId: number
        }
        vendorPricing: {
            open: boolean
            vendorId: number
        }
    }
    drawer: {
        vendorAnalyticsDrawer: {
            open: boolean
            vendorId: number
        }
    }
}

const closedFormState: WorkforceFormState = {
    modal: {
        vendorModal: {
            open: false,
            vendorId: -1,
        },
        vendorPricing: {
            open: false,
            vendorId: -1,
        },
    },
    drawer: {
        vendorAnalyticsDrawer: {
            open: false,
            vendorId: -1,
        },
    },
}
